import { useEffect, useState } from "react";
import styled from "styled-components";
import { Nav, OverlayTrigger, Tooltip } from "react-bootstrap";
import { SidebarOption } from "./SidebarOption";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown, faGlobe } from "@fortawesome/free-solid-svg-icons";
import { useBoundarySelectContext } from "../boundarySelection/BoundarySelectContext";

const PointerText = styled.div`
  min-height: 25px;
  cursor: pointer;
  display: flex;
  align-items: center;
  line-height: 0.9;
  padding-top: 5px;
  padding-bottom: 5px;
  padding-right: 10px;

  > .icon {
    width: 20px;
    padding-right: 3px;
  }
  :hover {
    background-color: var(--bs-primary-bg-subtle);
  }
`;

const NavItem = styled(Nav.Item)<any>`
  width: 100%;
  position: relative;

  > .nav {
    position: relative;
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    height: 0;
    overflow: hidden;
    transition: all 0.5s ease;
  }
  &.active > .nav {
    height: ${(props) => props["data-children"] * 25}px;
  }
  :hover > .nav {
    height: ${(props) => props["data-children"] * 25}px;
  }

  /* If the menu if ever 3+ layers deep this will be needed
  .nav:has(> .nav-item:hover)  {
    height: auto;
  }
  */
}
`;

interface VerticalFoldingMenuItemProps {
  option: SidebarOption;
  url: string;
  onSelect: (url: string) => void;
  depth?: number;
}

const VerticalFoldingMenuItem = ({
  option,
  url,
  onSelect,
  depth = 0,
}: VerticalFoldingMenuItemProps) => {
  const { boundaries } = useBoundarySelectContext();
  const [expanded, setExpanded] = useState(false);
  const leftPadding = depth;
  const fontSize = 1.15 - 0.15 * depth;
  const fontWeight = 500 - 100 * depth;

  useEffect(() => {
    setExpanded(isExpanded(option));
  }, [url, option]);

  const isExpanded = (option: SidebarOption): boolean => {
    if (option.url === url) {
      return true;
    } else if (option.children?.length) {
      return option.children.some((o) => isExpanded(o) === true);
    } else {
      return false;
    }
  };

  const isDisabled =
    option.isDisabled === true ||
    option.children?.every((child) => child.isDisabled === true);

  const renderContent = () => (
    <PointerText
      style={{
        paddingLeft: leftPadding + "rem",
        fontSize: fontSize + "rem",
        fontWeight: fontWeight,
      }}
      className={
        (option.url === url ? " bg-secondary-subtle" : "") +
        (isDisabled ? " text-body-tertiary" : " text-body")
      }
      onClick={() =>
        option.url && option.isDisabled !== true && onSelect(option.url)
      }
    >
      <span className="flex-fill">{option.label}</span>

      {!!option.children?.length && (
        <FontAwesomeIcon
          icon={faCaretDown}
          color="grey"
          style={{ fontSize: "12px" }}
        />
      )}
    </PointerText>
  );

  return (
    <NavItem
      data-children={option.children?.length ?? 0}
      className={expanded ? " active" : ""}
    >
      {(isDisabled && boundaries.length === 0) ? (
        <OverlayTrigger
          placement="right"
          overlay={
            <Tooltip id={`tooltip-${option.label}`}>
              A boundary must be selected.
            </Tooltip>
          }
        >
          {renderContent()}
        </OverlayTrigger>
      ) : (
        renderContent()
      )}
      {option.children && !!option.children.length && (
        <Nav className="flex-column">
          {option.children.map((option, index) => (
            <VerticalFoldingMenuItem
              key={index}
              option={option}
              url={url}
              onSelect={onSelect}
              depth={depth + 1}
            />
          ))}
        </Nav>
      )}
    </NavItem>
  );
};

export default VerticalFoldingMenuItem;
